import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircle2Icon } from 'lucide-react';
import { MeasureGuide } from '../data/module1';
interface MeasureOverlayProps {
  guide: MeasureGuide;
  measureNumber: number;
  measureText: string;
  onComplete: () => void;
  onClose?: () => void;
}
export function MeasureOverlay({
  guide,
  measureNumber,
  measureText,
  onComplete,
  onClose
}: MeasureOverlayProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = guide.steps.length;
  const progressPercent = (currentStep + 1) / totalSteps * 100;
  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const canGoBack = currentStep > 0;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" data-id="element-27">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-28" />

      {/* Fullscreen Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="relative bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-5xl max-h-[90vh] flex flex-col" data-id="element-29">
        {/* Header */}
        <div className="bg-[#284756] px-6 md:px-8 py-4 flex items-center justify-between flex-shrink-0" data-id="element-30">
          <div className="flex items-center space-x-4" data-id="element-31">
            <div className="w-10 h-10 rounded-full bg-[#8DC63F] text-white flex items-center justify-center font-bold text-base" data-id="element-32">
              {measureNumber}
            </div>
            <div data-id="element-33">
              <p className="text-green-400 text-xs font-semibold uppercase tracking-wider" data-id="element-34">
                Schutzmaßnahme
              </p>
              <h3 className="text-white font-bold text-lg md:text-xl leading-tight" data-id="element-35">
                {guide.title}
              </h3>
            </div>
          </div>
          <div className="flex items-center space-x-4" data-id="element-36">
            <div className="text-gray-400 text-xs hidden sm:block" data-id="element-37">
              <span data-id="element-38">
                Schritt {currentStep + 1}/{totalSteps}
              </span>
            </div>
            {onClose && <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1" data-id="element-39">
                <XIcon className="w-5 h-5" data-id="element-40" />
              </button>}
          </div>
        </div>

        {/* Mini Progress Bar */}
        <div className="w-full bg-gray-100 h-1.5 flex-shrink-0" data-id="element-41">
          <motion.div className="h-full bg-[#8DC63F]" animate={{
          width: `${progressPercent}%`
        }} transition={{
          duration: 0.4,
          ease: 'easeInOut'
        }} data-id="element-42" />
        </div>

        {/* Content Area — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6" data-id="element-43">
          <AnimatePresence mode="wait" data-id="element-44">
            <motion.div key={`step-${currentStep}`} initial={{
            opacity: 0,
            x: 40
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -40
          }} transition={{
            duration: 0.25
          }} data-id="element-45">
              <div className="flex items-start space-x-6" data-id="element-46">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#8DC63F] to-[#6fa030] text-white flex items-center justify-center font-bold text-2xl shadow-md" data-id="element-47">
                  {currentStep + 1}
                </div>
                <div className="flex-1 pt-1" data-id="element-48">
                  <h4 className="text-2xl font-bold text-[#1a1a2e] mb-3" data-id="element-49">
                    {guide.steps[currentStep].step}
                  </h4>
                  <p className="text-[#4a4a5a] text-lg leading-relaxed" data-id="element-50">
                    {guide.steps[currentStep].detail}
                  </p>
                  {guide.steps[currentStep].imageUrl && <div className="mt-5 rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-51">
                      <img src={guide.steps[currentStep].imageUrl} alt={guide.steps[currentStep].step} className="w-full object-contain max-h-[45vh]" data-id="element-52" />
                    </div>}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer Navigation */}
        <div className="px-6 md:px-8 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-white" data-id="element-53">
          <button onClick={handleBack} disabled={!canGoBack} className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${canGoBack ? 'text-[#4a4a5a] bg-gray-100 hover:bg-gray-200' : 'text-gray-300 bg-gray-50 cursor-not-allowed'}`} data-id="element-54">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-55" />
            <span data-id="element-56">Zurück</span>
          </button>

          {currentStep < totalSteps - 1 ? <button onClick={handleNext} className="flex items-center space-x-2 px-6 py-2.5 rounded-lg font-semibold text-white bg-[#8DC63F] hover:bg-[#7ab036] transition-colors shadow-md" data-id="element-57">
              <span data-id="element-58">Nächster Schritt</span>
              <ArrowRightIcon className="w-4 h-4" data-id="element-59" />
            </button> : <button onClick={onComplete} className="flex items-center space-x-2 px-6 py-2.5 rounded-lg font-semibold text-white bg-[#8DC63F] hover:bg-[#7ab036] transition-colors shadow-md" data-id="element-60">
              <CheckCircle2Icon className="w-5 h-5" data-id="element-61" />
              <span data-id="element-62">Maßnahme abschließen</span>
            </button>}
        </div>
      </motion.div>
    </motion.div>;
}